import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Upload, CheckCircle, XCircle, AlertCircle, FileText, Camera, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VerificationPage = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showConsent, setShowConsent] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [documentType, setDocumentType] = useState('drivers_license');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchVerificationStatus();
  }, [isAuthenticated, navigate]);

  const fetchVerificationStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/verification/status`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setVerificationStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch verification status:', error);
      toast({
        title: "Error",
        description: "Failed to load verification status",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast({
        title: "Invalid File Type",
        description: "Please upload JPG, PNG, or PDF files only",
        variant: "destructive"
      });
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Maximum file size is 10MB",
        variant: "destructive"
      });
      return;
    }

    setSelectedFile(file);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast({
        title: "No File Selected",
        description: "Please select a file to upload",
        variant: "destructive"
      });
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('document_type', documentType);

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/verification/upload-id`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          withCredentials: true
        }
      );

      toast({
        title: "Success",
        description: response.data.message
      });

      // Refresh status
      await fetchVerificationStatus();
      setSelectedFile(null);
      setFilePreview(null);

    } catch (error) {
      console.error('Upload failed:', error);
      toast({
        title: "Upload Failed",
        description: error.response?.data?.detail || "Failed to upload ID. Please try again.",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleCancelSubmission = async () => {
    if (!window.confirm('Are you sure you want to cancel your ID verification? Your submitted document will be deleted.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/verification/cancel-submission`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      toast({
        title: "Cancelled",
        description: "Your ID verification has been cancelled"
      });

      await fetchVerificationStatus();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to cancel submission",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mint-500"></div>
      </div>
    );
  }

  const status = verificationStatus?.govIdVerificationStatus || 'not_submitted';

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/profile')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Profile
          </button>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">ID Verification</h1>
              <p className="text-gray-600 mt-1">Get your verified badge</p>
            </div>
          </div>
        </div>

        {/* Status Display */}
        {status === 'approved' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
            <div className="flex items-start gap-4">
              <CheckCircle className="w-8 h-8 text-green-600 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-green-900 mb-2">
                  Your ID is Verified! ✓
                </h3>
                <p className="text-green-700">
                  Your government ID has been verified. You now have a verified badge on your profile.
                </p>
                <p className="text-sm text-green-600 mt-2">
                  Verified on: {new Date(verificationStatus.govIdVerifiedAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        )}

        {status === 'pending' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-8 h-8 text-yellow-600 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-yellow-900 mb-2">
                  Verification Pending
                </h3>
                <p className="text-yellow-700 mb-4">
                  Your ID has been submitted and is currently under review by our team. 
                  This usually takes 24-48 hours.
                </p>
                <p className="text-sm text-yellow-600">
                  Submitted on: {new Date(verificationStatus.govIdSubmittedAt).toLocaleDateString()}
                </p>
                <button
                  onClick={handleCancelSubmission}
                  className="mt-4 text-sm text-yellow-700 hover:text-yellow-900 underline"
                >
                  Cancel submission
                </button>
              </div>
            </div>
          </div>
        )}

        {status === 'rejected' && (
          <div className="bg-cream-100 border border-red-200 rounded-lg p-6 mb-8">
            <div className="flex items-start gap-4">
              <XCircle className="w-8 h-8 text-navy-500 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-red-900 mb-2">
                  Verification Rejected
                </h3>
                <p className="text-red-700 mb-2">
                  Your ID verification was not approved. Please review the reason below and submit a new document.
                </p>
                {verificationStatus.govIdRejectionReason && (
                  <div className="bg-red-100 rounded p-3 mt-3">
                    <p className="text-sm text-red-800">
                      <strong>Reason:</strong> {verificationStatus.govIdRejectionReason}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        {(status === 'not_submitted' || status === 'rejected') && (
          <>
            {/* Consent Section */}
            {showConsent && (
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Why Verify Your ID?
                </h2>
                <ul className="space-y-3 text-gray-700 mb-6">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Build trust with potential matches</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Get a verified badge on your profile</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Confirm your identity and age</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Reduce fake profiles in the community</span>
                  </li>
                </ul>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <h3 className="font-semibold text-blue-900 mb-2">Your Privacy</h3>
                  <ul className="space-y-2 text-sm text-blue-800">
                    <li>✓ Your ID is encrypted and stored securely</li>
                    <li>✓ Only authorized admins can view your ID</li>
                    <li>✓ Your ID is automatically deleted 30 days after verification</li>
                    <li>✓ Your ID is never shared with other users</li>
                  </ul>
                </div>

                <button
                  onClick={() => setShowConsent(false)}
                  className="w-full bg-mint-500 text-white py-3 rounded-lg font-semibold hover:bg-mint-600 transition-colors"
                >
                  I Understand - Continue to Upload
                </button>
              </div>
            )}

            {/* Upload Section */}
            {!showConsent && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Upload Your Government ID
                </h2>

                {/* Important Matching Information Message */}
                <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 mb-6">
                  <h3 className="font-bold text-amber-800 mb-3 flex items-center gap-2">
                    ⚠️ Important: Your ID Must Match Your Profile
                  </h3>
                  <p className="text-amber-700 text-sm mb-3">
                    For your verification to be approved, the following information on your ID <strong>must match</strong> your profile:
                  </p>
                  
                  {/* Profile Picture + Info Grid */}
                  <div className="flex gap-4 items-start">
                    {/* Profile Picture */}
                    <div className="bg-white rounded-lg p-3 border border-amber-200 flex-shrink-0">
                      <p className="text-xs text-amber-600 font-medium mb-2 text-center">PROFILE PHOTO</p>
                      <img 
                        src={user?.image || 'https://via.placeholder.com/100'} 
                        alt="Profile" 
                        className="w-24 h-24 rounded-lg object-cover border-2 border-amber-200"
                      />
                      <p className="text-xs text-amber-600 mt-2 text-center">Must match ID photo</p>
                    </div>
                    
                    {/* Info Grid */}
                    <div className="flex-1 grid grid-cols-2 gap-3">
                      <div className="bg-white rounded-lg p-3 border border-amber-200">
                        <p className="text-xs text-amber-600 font-medium">FIRST & LAST NAME</p>
                        <p className="font-semibold text-gray-900">{user?.firstName} {user?.lastName}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-amber-200">
                        <p className="text-xs text-amber-600 font-medium">DATE OF BIRTH</p>
                        <p className="font-semibold text-gray-900">
                          {user?.dob 
                            ? new Date(user.dob).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                            : 'Not set in profile'
                          }
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-amber-200">
                        <p className="text-xs text-amber-600 font-medium">PROVINCE/STATE</p>
                        <p className="font-semibold text-gray-900">{user?.province || user?.location?.split(',')[1]?.trim() || 'Not set'}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-amber-200">
                        <p className="text-xs text-amber-600 font-medium">COUNTRY</p>
                        <p className="font-semibold text-gray-900">{user?.country || 'Not set'}</p>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-amber-700 text-sm mt-3">
                    ❌ If your ID doesn't match, your verification will be <strong>rejected</strong>.
                    <br />
                    ✏️ <a href="/edit-profile" className="underline hover:text-amber-900">Update your profile</a> before submitting if needed.
                  </p>
                </div>

                {/* Document Type Selector */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Document Type
                  </label>
                  <select
                    value={documentType}
                    onChange={(e) => setDocumentType(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                  >
                    <option value="drivers_license">Driver's License</option>
                    <option value="passport">Passport</option>
                    <option value="pr_card">Permanent Resident Card</option>
                    <option value="provincial_id">Provincial ID Card</option>
                  </select>
                </div>

                {/* File Upload */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Document
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-mint-500 transition-colors">
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,application/pdf"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="file-upload"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 mb-2">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-sm text-gray-500">
                        JPG, PNG or PDF (max 10MB)
                      </p>
                    </label>
                  </div>

                  {/* File Preview */}
                  {selectedFile && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-4">
                        {filePreview ? (
                          <img src={filePreview} alt="Preview" className="w-24 h-24 object-cover rounded" />
                        ) : (
                          <FileText className="w-12 h-12 text-gray-400" />
                        )}
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{selectedFile.name}</p>
                          <p className="text-sm text-gray-500">
                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedFile(null);
                            setFilePreview(null);
                          }}
                          className="text-navy-500 hover:text-navy-600"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Requirements */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Requirements:</h3>
                  <ul className="space-y-1 text-sm text-gray-700">
                    <li>• Photo must be clear and all text readable</li>
                    <li>• Document must not be expired</li>
                    <li>• Full document must be visible (all edges)</li>
                    <li>• No edited or tampered images</li>
                  </ul>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowConsent(true)}
                    className="flex-1 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleUpload}
                    disabled={!selectedFile || uploading}
                    className="flex-1 py-3 bg-mint-500 text-white rounded-lg font-semibold hover:bg-mint-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {uploading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Camera className="w-5 h-5" />
                        Submit for Verification
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VerificationPage;
